import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';

interface EventInfoSidebarProps {
  eventId: string;
  startDate: string;
  endDate: string | null;
  price: number | null;
  currency: string | null;
  capacity: number | null;
  registrationCount: number;
  locationType: string;
  socialLinks: any;
  eventTitle?: string;
  alreadyRegistered?: boolean;
}

export default function EventInfoSidebar({
  eventId,
  startDate,
  endDate,
  price,
  currency,
  capacity,
  registrationCount,
  locationType,
  socialLinks,
  eventTitle = '',
  alreadyRegistered = false,
}: EventInfoSidebarProps) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const spotsLeft = capacity ? capacity - registrationCount : null;
  const isFull = spotsLeft === 0;

  // Show sticky bar after user scrolls past the hero
  useEffect(() => {
    const handleScroll = () => {
      setStickyVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Use the edge function URL for sharing so social crawlers get proper OG image previews.
  // The edge function detects crawlers and serves pre-rendered HTML with OG tags,
  // then redirects real users back to the SPA event page.
  const shareUrl = `https://eakprftqkrtkhllpzbxw.supabase.co/functions/v1/redirect-event?id=${eventId}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleRegisterClick = () => {
    if (authLoading) return;

    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    setIsRegistering(true);
    setTimeout(() => {
      navigate(`/event/${eventId}/register`);
    }, 600);
  };

  const parsedSocialLinks = Array.isArray(socialLinks)
    ? socialLinks
    : socialLinks && typeof socialLinks === 'object'
    ? Object.entries(socialLinks).map(([platform, url]) => ({ platform, url }))
    : [];

  return (
    <div className="lg:sticky lg:top-8 space-y-4">
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowLoginPrompt(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-8 animate-[fadeInUp_0.3s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1.5">Account Required</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                You need to create an account or log in before registering for this event.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate(`/login?redirect=/event/${eventId}/register`)}
                className="w-full py-3 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-semibold text-sm rounded-xl hover:shadow-lg hover:shadow-teal-500/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-login-box-line"></i>
                  Log In
                </span>
              </button>
              <button
                onClick={() => navigate(`/signup?redirect=/event/${eventId}/register`)}
                className="w-full py-3 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-user-add-line"></i>
                  Create Account
                </span>
              </button>
            </div>

            <button
              onClick={() => setShowLoginPrompt(false)}
              className="w-full mt-3 py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── MOBILE STICKY BOTTOM BAR ── */}
      {!alreadyRegistered && !isFull && (
        <div
          className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
            stickyVisible ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            {/* Price pill */}
            <div className="flex-shrink-0">
              <p className="text-xs text-gray-400 leading-none mb-0.5">Price</p>
              <p className="text-base font-bold text-gray-900 leading-none">
                {price && price > 0 ? (
                  <>{currency || 'USD'} {price.toFixed(2)}</>
                ) : (
                  <span className="text-teal-600">Free</span>
                )}
              </p>
            </div>
            {/* Register button */}
            <button
              onClick={handleRegisterClick}
              disabled={isRegistering}
              className={`flex-1 py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all whitespace-nowrap cursor-pointer relative overflow-hidden group ${
                isRegistering
                  ? 'bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 text-white scale-95'
                  : 'bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white active:scale-[0.98]'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                {isRegistering ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="ri-ticket-line text-base"></i>
                    Register Now
                    <i className="ri-arrow-right-line text-base"></i>
                  </>
                )}
              </span>
            </button>
          </div>
          {/* Safe area spacer for phones with home indicator */}
          <div className="bg-white h-safe-bottom" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}></div>
        </div>
      )}

      {/* Already registered — mobile sticky bar */}
      {alreadyRegistered && (
        <div
          className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
            stickyVisible ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-3">
              <div className="flex-1 py-3 rounded-xl font-bold text-sm bg-emerald-50 border-2 border-emerald-200 text-emerald-700 flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-fill text-lg text-emerald-500"></i>
                Already Registered
              </div>
              <button
                onClick={() => navigate('/dashboard/my-registrations')}
                className="flex-shrink-0 py-3 px-4 rounded-xl text-sm font-medium text-teal-600 border border-teal-200 hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-file-list-3-line text-base"></i>
                My Tickets
              </button>
            </div>
          </div>
          <div className="bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}></div>
        </div>
      )}

      {/* Price & Register Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-lg shadow-gray-200/50 p-4 sm:p-5 md:p-6">
        {/* Price */}
        <div className="mb-4 sm:mb-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Price
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">
            {price && price > 0 ? (
              <>
                <span className="text-base sm:text-lg align-top text-gray-500">
                  {currency || 'USD'}
                </span>{' '}
                {price.toFixed(2)}
              </>
            ) : (
              <span className="text-teal-600">Free</span>
            )}
          </p>
        </div>

        {/* Capacity */}
        {capacity && (
          <div className="mb-4 sm:mb-5">
            <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
              <span className="text-gray-600">{registrationCount} registered</span>
              <span className="text-gray-500">{capacity} total</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  isFull
                    ? 'bg-red-400'
                    : spotsLeft && spotsLeft < 10
                    ? 'bg-amber-400'
                    : 'bg-teal-500'
                }`}
                style={{ width: `${Math.min((registrationCount / capacity) * 100, 100)}%` }}
              ></div>
            </div>
            {spotsLeft !== null && (
              <p
                className={`text-xs mt-1.5 font-medium ${
                  isFull ? 'text-red-500' : spotsLeft < 10 ? 'text-amber-600' : 'text-teal-600'
                }`}
              >
                {isFull ? 'No spots remaining' : `${spotsLeft} spots left`}
              </p>
            )}
          </div>
        )}

        {/* Register Button */}
        {alreadyRegistered ? (
          <div className="space-y-2.5">
            <div className="w-full py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base bg-emerald-50 border-2 border-emerald-200 text-emerald-700 flex items-center justify-center gap-2">
              <i className="ri-checkbox-circle-fill text-lg text-emerald-500"></i>
              Already Registered
            </div>
            <button
              onClick={() => navigate('/dashboard/my-registrations')}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              <i className="ri-file-list-3-line text-base"></i>
              View My Registrations
            </button>
          </div>
        ) : (
          <button
            onClick={handleRegisterClick}
            disabled={isFull || isRegistering}
            className={`w-full py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base tracking-wide transition-all whitespace-nowrap cursor-pointer relative overflow-hidden group ${
              isFull
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : isRegistering
                ? 'bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 text-white scale-95'
                : 'bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-[0.98]'
            }`}
          >
            {!isFull && !isRegistering && (
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
            )}
            <span className="relative flex items-center justify-center gap-2">
              {isFull ? (
                <>
                  <i className="ri-close-circle-line text-base sm:text-lg"></i>
                  Event Full
                </>
              ) : isRegistering ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Opening Registration...</span>
                  <span className="sm:hidden">Loading...</span>
                </>
              ) : (
                <>
                  <i className="ri-ticket-line text-base sm:text-lg"></i>
                  Register Now
                  <i className="ri-arrow-right-line text-base sm:text-lg group-hover:translate-x-1 transition-transform"></i>
                </>
              )}
            </span>
          </button>
        )}
        <p className="text-center text-xs text-gray-400 mt-2 sm:mt-3 flex items-center justify-center gap-1.5">
          <i className="ri-shield-check-line text-teal-500"></i>
          <span className="hidden sm:inline">Instant confirmation • Secure booking</span>
          <span className="sm:hidden">Secure booking</span>
        </p>
      </div>

      {/* Quick Info Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5 md:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Event Details</h3>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-line text-teal-600 text-sm sm:text-base"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{formatDate(startDate)}</p>
              <p className="text-xs text-gray-500">
                {formatTime(startDate)}
                {endDate ? ` — ${formatTime(endDate)}` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i
                className={`${
                  locationType === 'online' ? 'ri-global-line' : 'ri-map-pin-line'
                } text-teal-600 text-sm sm:text-base`}
              ></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900">
                {locationType === 'online' ? 'Online Event' : 'In-Person'}
              </p>
              <p className="text-xs text-gray-500">
                {locationType === 'online' ? 'Join from anywhere' : 'Physical venue'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-group-line text-teal-600 text-sm sm:text-base"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900">{registrationCount} Attendees</p>
              <p className="text-xs text-gray-500">People registered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Share Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5 md:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Share This Event</h3>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${eventTitle}\n${shareUrl}`)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex flex-col items-center gap-1.5 py-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer group"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-green-500 rounded-full text-white group-hover:scale-110 transition-transform">
              <i className="ri-whatsapp-line text-lg"></i>
            </div>
            <span className="text-xs font-medium text-green-700">WhatsApp</span>
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(eventTitle)}&url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex flex-col items-center gap-1.5 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer group"
          >
            <div className="w-9 h-9 flex items-center justify-center bg-gray-900 rounded-full text-white group-hover:scale-110 transition-transform">
              <i className="ri-twitter-x-line text-lg"></i>
            </div>
            <span className="text-xs font-medium text-gray-700">Twitter</span>
          </a>
          <button
            onClick={handleCopyLink}
            className="flex flex-col items-center gap-1.5 py-3 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors cursor-pointer group"
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-full text-white group-hover:scale-110 transition-transform ${copied ? 'bg-teal-600' : 'bg-teal-500'}`}>
              <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-lg`}></i>
            </div>
            <span className="text-xs font-medium text-teal-700">{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>
        {parsedSocialLinks.length > 0 && (
          <>
            <div className="border-t border-gray-100 pt-3 mt-1">
              <p className="text-xs text-gray-400 mb-2">Organizer socials</p>
              <div className="flex items-center gap-2">
                {parsedSocialLinks.map((link: any, idx: number) => {
                  const iconMap: Record<string, string> = {
                    facebook: 'ri-facebook-fill',
                    twitter: 'ri-twitter-x-fill',
                    instagram: 'ri-instagram-fill',
                    linkedin: 'ri-linkedin-fill',
                  };
                  const icon = iconMap[link.platform] || 'ri-link';
                  return (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-gray-50 rounded-lg hover:bg-teal-50 hover:text-teal-600 text-gray-500 transition-colors cursor-pointer"
                    >
                      <i className={`${icon} text-sm sm:text-base`}></i>
                    </a>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom padding on mobile so sticky bar doesn't cover content */}
      <div className="lg:hidden h-20"></div>
    </div>
  );
}
